/**
 * Validates if a filter is complete and ready for API calls
 * A filter is considered valid if:
 * - field is not empty
 * - operator is not empty (except for "known" and "unknown" operators which don't need values)
 * - value is provided (unless operator is "known" or "unknown")
 */
export const isFilterValid = (
  field?: string,
  operator?: string,
  value?: any,
): boolean => {
  // Field is required
  if (!field || field === "") {
    return false;
  }

  // Operator is required
  if (!operator || operator === "") {
    return false;
  }

  // For "known" and "unknown" operators, value is not required
  if (operator === "known" || operator === "unknown") {
    return true;
  }

  // For other operators, value is required
  // Check if value is not undefined, null, or empty string
  if (value === undefined || value === null || value === "") {
    // Exception: empty array is valid for "in" operator (user might be selecting)
    if (operator === "in" && Array.isArray(value)) {
      return true;
    }
    return false;
  }

  // For range operators, check if both start and end are provided and start > end
  if (operator === "range" || operator === "between") {
    if (typeof value === "object" && value !== null) {
      if (value.start === undefined || value.end === undefined) {
        return false;
      }
      // Validate: start must be less than end
      // For dates (ISO strings), compare as dates
      // For numbers, compare as numbers
      if (typeof value.start === "string" && typeof value.end === "string") {
        // Assume ISO date strings
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        return startDate < endDate;
      }
      // For numbers
      return Number(value.start) < Number(value.end);
    }
    return false;
  }

  return true;
};

/**
 * Filters out incomplete predicates from a filter group
 * This is useful before making API calls to prevent errors
 */
export const filterIncompletePredicates = (predicates: any[]): any[] => {
  if (!Array.isArray(predicates)) {
    return [];
  }

  return predicates.filter((predicate: any) => {
    if (!predicate || typeof predicate !== "object") {
      return false;
    }

    // If this predicate has nested predicates (filter group), recursively filter them
    if (Array.isArray(predicate.predicates)) {
      const filteredNested = filterIncompletePredicates(predicate.predicates);
      // Only keep the group if it has at least one valid predicate
      return filteredNested.length > 0;
    }

    // For regular predicates, validate using isFilterValid
    return isFilterValid(
      predicate.field,
      predicate.operator,
      predicate.value,
    );
  });
};
